import { enqueue, type MosDB, type SyncedRows, type SyncedTable } from "./db";

// Every synced write goes through here: the row and its outbox entry are
// written in one transaction, so a crash can never leave a change that is
// saved locally but never sent (or sent but not saved).

type Listener = () => void;
const listeners = new Set<Listener>();

/** Called after each local write; the sync runner uses it to flush soon. */
export function onLocalWrite(fn: Listener): () => void {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

/**
 * An edit time that is always later than the row's previous one, even if the
 * device clock went backwards, so the server's "newer wins" keeps this edit.
 */
export function nextUpdatedAt(previous?: string): string {
	const now = Date.now();
	const prev = previous ? Date.parse(previous) : 0;
	return new Date(Math.max(now, prev + 1)).toISOString();
}

type Rows = SyncedRows;

export async function save<T extends SyncedTable>(
	table: T,
	row: Rows[T],
	database: MosDB,
): Promise<Rows[T]> {
	await database.transaction("rw", database.table(table), database.outbox, async () => {
		await database.table(table).put(row);
		await enqueue(
			{ table, entityId: row.id, op: "upsert", payload: row, updatedAt: row.updated_at },
			database,
		);
	});
	listeners.forEach((fn) => fn());
	return row;
}

export async function update<T extends SyncedTable>(
	table: T,
	id: string,
	patch: Partial<Rows[T]>,
	database: MosDB,
): Promise<Rows[T]> {
	// Read and write in one transaction so two quick edits cannot interleave.
	return database.transaction("rw", database.table(table), database.outbox, async () => {
		const current = (await database.table(table).get(id)) as Rows[T] | undefined;
		if (!current || current.deleted_at) throw new Error(`${table} ${id} not found`);
		const next = { ...current, ...patch, id, updated_at: nextUpdatedAt(current.updated_at) };
		return save(table, next, database);
	});
}

export async function remove(table: SyncedTable, id: string, database: MosDB): Promise<void> {
	await database.transaction("rw", database.table(table), database.outbox, async () => {
		const current = await database.table(table).get(id);
		if (!current || current.deleted_at) return;
		const now = nextUpdatedAt(current.updated_at);
		// Keep a local tombstone so lists hide the row until the server confirms.
		await database.table(table).put({ ...current, deleted_at: now, updated_at: now });
		await enqueue({ table, entityId: id, op: "delete", payload: null, updatedAt: now }, database);
	});
	listeners.forEach((fn) => fn());
}
